/**
 * LA CONFIRMATION DE PRISE EN CHARGE — l'écran, 23/09/2026.
 *
 * Le document envoyé au CLIENT après la saisie d'un ordre. À ne pas confondre
 * avec la confirmation d'AFFRÈTEMENT, reçue d'un donneur d'ordre.
 *
 *
 * CET ÉCRAN EST LA FEUILLE ELLE-MÊME
 * ---------------------------------------------------------------------------
 * Stafa ne sait pas fabriquer un PDF : aucune bibliothèque PDF, aucun binding
 * Browser Rendering dans `apps/api/wrangler.jsonc`, et depuis le 11/09 c'est
 * Pennylane qui imprime la facture. Un Worker Cloudflare ne lance pas de
 * chromium. Arbitrage du 23/09 : LE NAVIGATEUR IMPRIME. Tout ce qui est ici
 * est donc du balisage et du CSS, et `window.print()` fait le fichier.
 *
 * D'où le gabarit repris de `docs/maquettes/confirmation-prise-en-charge.html`,
 * lui-même relevé au millimètre sur le modèle de facture — feuille de
 * 794 x 1123 px, marges 32/30/0/29, DejaVu Sans à 8,4 px, le bloc émetteur en
 * serif, le bleu #0756a5 mesuré dans le PDF, le talon à deux moitiés et la
 * bande qui déborde les marges. Un client doit reconnaître la maison.
 *
 * L'IDENTITÉ DE TNS EST EN DUR : la table `societes` ne porte que
 * `raison_sociale`, `siret` et `etat`. Le jour où elle portera l'adresse et la
 * TVA, `TNS` ci-dessous est le seul objet à remplacer.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { LogoTns } from './LogoTns.js';
import { BoiteEnvoi } from './BoiteEnvoi.js';

import {
  centimes,
  creneau,
  euros,
  indexation,
  jourCourt,
  jourLong,
  lireConfirmation,
  repere,
  type ConfirmationLue,
  type DestinatairesProposes,
  type EtapeConfirmation,
} from './confirmation.js';

/** L'émetteur, repris mot pour mot du modèle de facture. */
const TNS = {
  rs: 'TNS LOGISTIQUE',
  l1: '2 route de Gasville — 28630 Nogent-le-Phaye',
  l2: 'SARL au capital de 17 048 € — RCS Chartres 910 696 004',
  l3: 'SIRET 910 696 004 00027 — NAF 4941A',
  l4: 'Identifiant TVA : FR89 910 696 004',
  service: 'Service exploitation',
  mail: 'exploitation@tns-logistique.com',
  // Le standard finit par 15 pour la facturation ; l'exploitation a la ligne 13.
  tel: 'Tél. : +33 2 34 40 33 13',
};

/** La hauteur d'une page A4 à 96 ppp, la même que la feuille au minimum. */
const HAUTEUR_A4 = 1123;

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'refus'; readonly message: string }
  | { readonly nom: 'prete'; readonly lue: ConfirmationLue };

interface Proprietes {
  readonly jeton: () => Promise<string | null>;
  readonly otId: string;
  readonly quandFermer: () => void;
  readonly quandAccesPerdu: () => void;
  /**
   * LA CONFIRMATION DÉJÀ LUE, pour l'aperçu — même mécanisme que
   * `EcranResultat` et son `dejaLu` (apercu.tsx). Quand elle est fournie,
   * aucun appel à l'API n'est fait.
   *
   * ELLE EXISTE POUR QU'ON PUISSE REGARDER CET ÉCRAN, et c'est une leçon
   * payée plusieurs fois sur ce chantier : les défauts d'habillage ne se
   * voient ni dans le code ni dans les tests. Deux des trois défauts du lot
   * A-bis ont été trouvés à l'œil.
   */
  readonly dejaLu?: ConfirmationLue;
  /** Les destinataires déjà lus, passés au panneau d'envoi. Aperçu seulement. */
  readonly destinatairesDejaLus?: DestinatairesProposes;
  /** Ouvre le panneau d'envoi dès l'affichage. Aperçu seulement. */
  readonly envoiOuvertAuDepart?: boolean;
}

/**
 * Exportée le 21/10/2026 pour l'ORDRE D'AFFRÈTEMENT, qui imprime le même
 * tableau. Deux formateurs de nombres donneraient deux documents qui se
 * ressemblent sans être pareils.
 */
export function nombre(valeur: number, decimales = 0): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valeur);
}

/**
 * LA FEUILLE EST SORTIE DE L'ARBRE DE LA FICHE, dans `document.body`.
 *
 * Pas par goût : à L'IMPRESSION, la feuille est mise à `position: absolute` en
 * haut à gauche de la page. Un élément absolu se cale sur son premier ancêtre
 * POSITIONNÉ ; laissée dans la fiche, la feuille se serait calée sur le
 * panneau ou le bandeau, et serait sortie décalée — d'une quantité qui dépend
 * de l'écran, donc invisible tant qu'on n'imprime pas.
 *
 * Le portail est le premier du dépôt. `.application` est reposé dessus pour
 * que les règles de la feuille, toutes préfixées, s'appliquent.
 */
function Voile({ children }: { readonly children: ReactNode }) {
  return createPortal(
    <div className="application conf-racine">
      <div className="conf-voile">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * Une ligne d'arrêt : date · repère · lieu détaillé · créneau · charge.
 *
 * Exportée le 21/10/2026 : l'ordre d'affrètement imprime EXACTEMENT le même
 * tableau. Le sous-traitant et le client doivent lire les mêmes dates, les
 * mêmes adresses et les mêmes créneaux — deux composants jumeaux finiraient
 * par diverger à la première correction faite d’un seul côté.
 */
export function Arret({
  e,
  combien,
}: {
  readonly e: EtapeConfirmation;
  readonly combien: number;
}) {
  const q = e.quantite === null ? null : Number(e.quantite);
  const ml = e.ml_unitaire === null || q === null ? null : q * Number(e.ml_unitaire);
  const etranger = e.site_pays !== null && e.site_pays !== 'France';
  return (
    <tr>
      <td>{jourCourt(e.jour)}</td>
      <td className="conf-ref">{repere(e.type, e.rang, combien)}</td>
      <td className="conf-det">
        <b>{e.site_nom}</b>
        <br />
        {e.site_adresse === null ? '' : `${e.site_adresse} — `}
        {e.site_code_postal ?? ''} {e.site_ville ?? ''}
        {etranger ? ` (${e.site_pays ?? ''})` : ''}
        {e.note !== null && <span className="conf-instr">Instructions : {e.note}</span>}
      </td>
      <td>{creneau(e)}</td>
      <td className="conf-n">
        {q === null ? '—' : `${nombre(q)} ${e.support ?? ''}`}
        {(e.poids !== null || ml !== null) && (
          <>
            <br />
            <span className="conf-charge">
              {e.poids === null ? '' : `${nombre(e.poids)} kg`}
              {e.poids !== null && ml !== null ? ' — ' : ''}
              {ml === null ? '' : `${nombre(ml, 2)} ml`}
            </span>
          </>
        )}
      </td>
    </tr>
  );
}

export function EcranConfirmation({
  jeton,
  otId,
  quandFermer,
  quandAccesPerdu,
  dejaLu,
  destinatairesDejaLus,
  envoiOuvertAuDepart,
}: Proprietes) {
  const [etat, setEtat] = useState<Etat>(
    dejaLu === undefined ? { nom: 'chargement' } : { nom: 'prete', lue: dejaLu },
  );
  /** Le panneau d'envoi, replié par défaut — lot A-bis, 29/10/2026. */
  const [envoiOuvert, setEnvoiOuvert] = useState(envoiOuvertAuDepart === true);

  const charger = useCallback(async () => {
    // L'aperçu passe la confirmation : aucun appel, aucun jeton.
    if (dejaLu !== undefined) return;

    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const resultat = await lireConfirmation(j, otId);
    if (resultat.etat !== 'ok') {
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setEtat({
        nom: 'refus',
        message: resultat.message ?? 'La confirmation n’a pas pu être lue.',
      });
      return;
    }
    setEtat({ nom: 'prete', lue: resultat.valeur });
  }, [jeton, otId, quandAccesPerdu, dejaLu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // ÉCHAP FERME, comme partout ailleurs dans le logiciel.
  useEffect(() => {
    const touche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => {
      window.removeEventListener('keydown', touche);
    };
  }, [quandFermer]);

  if (etat.nom !== 'prete') {
    return (
      <Voile>
        <div className="conf-outils">
          <b>Confirmation de prise en charge</b>
          <button className="bouton" type="button" onClick={quandFermer}>
            Fermer
          </button>
        </div>
        <p className={etat.nom === 'refus' ? 'bandeau-refus' : 'attente'}>
          {etat.nom === 'refus' ? etat.message : 'Chargement…'}
        </p>
      </Voile>
    );
  }

  return (
    <Voile>
      {/* LA BARRE, refondue le 29/10/2026 sur maquette validée.
          Avant : « Confirmation 26090175 » puis Fermer, Imprimer et Envoyer —
          trois boutons de même poids, « Fermer » en tête, suivis d'une phrase
          d'aide qui faisait passer la barre sur deux lignes.
          Après : l'identité à gauche, les actions à droite, UNE SEULE
          primaire, et la phrase d'aide descendue dans le panneau où elle sert. */}
      <div className="conf-outils">
        <button
          className="conf-retour"
          type="button"
          title="Fermer"
          aria-label="Fermer"
          onClick={quandFermer}
        >
          ←
        </button>
        <span className="conf-identite">
          <span className="quoi">Confirmation de prise en charge</span>
          <span className="qui">
            n° <b>{etat.lue.ordre.numero}</b>
            {etat.lue.client.nom !== '' && <> · {etat.lue.client.nom}</>}
            {etat.lue.ordre.reference_client !== null && (
              <> · votre réf. <b>{etat.lue.ordre.reference_client}</b></>
            )}
          </span>
        </span>
        <span className="conf-actions">
          {/* L'IMPRESSION RESTE LE PREMIER GESTE : Stafa ne fabrique pas le
              PDF, c'est le navigateur. On ne peut pas envoyer sans l'avoir
              imprimé — l'ordre de lecture le dit sans qu'on l'écrive. */}
          {/* `bouton-discret` ET NON `bouton` SEUL. Mesuré au lieu d'être
              supposé : dans ce dépôt `.bouton` est déjà un bouton PLEIN, bleu
              foncé sur texte blanc (rgb(15, 76, 129)). La première version de
              cette barre mettait les deux actions en `bouton` et croyait les
              hiérarchiser avec `conf-primaire` — à l'écran, deux boutons bleus
              se disputaient le regard. `bouton-discret` est la variante
              secondaire du dépôt, et de loin la plus employée (99 usages). */}
          <button
            className="bouton bouton-discret"
            type="button"
            onClick={() => {
              window.print();
            }}
          >
            Imprimer en PDF
          </button>
          {/* LE BLEU SUIT L'ACTION, PAS LE BOUTON. Constaté en regardant
              l'écran : une fois le panneau ouvert, le bouton dit « Masquer
              l'envoi » — et restait bleu. Masquer n'est pas l'action
              principale, et deux boutons de même poids se disputaient le
              regard pendant qu'on préparait un envoi. */}
          <button
            className={
              envoiOuvert ? 'bouton bouton-discret' : 'bouton conf-primaire'
            }
            type="button"
            onClick={() => {
              setEnvoiOuvert((avant) => !avant);
            }}
          >
            {envoiOuvert ? 'Masquer l’envoi' : 'Envoyer au client'}
          </button>
        </span>
      </div>

      {/* DEUX COLONNES : le document à gauche, le panneau à côté. La version
          précédente dépliait le panneau AU-DESSUS de la feuille et la poussait
          hors de l'écran — on préparait l'envoi sans plus voir ce qu'on
          envoyait. */}
      <div className={envoiOuvert ? 'conf-scene conf-scene-envoi' : 'conf-scene'}>
        <FeuilleConfirmation lue={etat.lue} />

        {envoiOuvert && (
          <BoiteEnvoi
            jeton={jeton}
            otId={otId}
            numero={String(etat.lue.ordre.numero)}
            quandFermer={() => {
              setEnvoiOuvert(false);
            }}
            quandAccesPerdu={quandAccesPerdu}
            {...(destinatairesDejaLus === undefined
              ? {}
              : { dejaLu: destinatairesDejaLus })}
          />
        )}
      </div>
    </Voile>
  );
}

/**
 * LA FEUILLE SEULE, sans le chargement ni la barre d'outils.
 *
 * Séparée du chargeur le 23/09/2026 pour la même raison que `VueOrdres` l'est
 * d'`EcranOrdres` : elle NE FAIT QUE DESSINER. On lui donne ce que l'API a
 * rendu, elle rend du balisage — ce qui la rend montrable dans l'aperçu, donc
 * VÉRIFIABLE sans base ni jeton.
 */
export function FeuilleConfirmation({ lue }: { readonly lue: ConfirmationLue }) {
  /**
   * LE NOMBRE DE PAGES EST MESURÉ, PAS SUPPOSÉ.
   *
   * Au-delà de quatre arrêts la feuille dépasse 1123 px et le navigateur la
   * coupe : « Page 1/1 » serait alors un mensonge imprimé sur un document
   * envoyé au client.
   */
  const [pages, setPages] = useState(2);
  const feuille = useRef<HTMLDivElement | null>(null);
  const verso = useRef<HTMLDivElement | null>(null);

  // Mesure APRÈS la mise en page, avant que l'écran ne soit peint : un
  // `useEffect` ferait clignoter « Page 1/1 » puis « Page 1/2 ».
  //
  // LES DEUX FEUILLES SONT MESURÉES, PAS SEULEMENT LA PREMIÈRE — 28/10/2026.
  // Le verso porte un texte fixe qui tient aujourd'hui sur une page ; une
  // mention ajoutée demain le ferait déborder, et « Page 2/2 » deviendrait le
  // mensonge que ce calcul existe précisément pour éviter.
  //
  // ET SURTOUT PAS `.conf-cadre` : il porte `padding: 18px 0 40px` à l'écran
  // et rien à l'impression. Le mesurer donnerait trois pages pour deux.
  useLayoutEffect(() => {
    const recto = feuille.current;
    const dos = verso.current;
    if (recto === null || dos === null) return;
    const compte = (e: HTMLDivElement) =>
      Math.max(1, Math.ceil(e.getBoundingClientRect().height / HAUTEUR_A4));
    setPages(compte(recto) + compte(dos));
  }, [lue]);

  const { ordre, client, etapes, indexation: clause } = lue;
  const chargements = etapes.filter((e) => e.type === 'chargement');
  const livraisons = etapes.filter((e) => e.type === 'livraison');
  const idx = indexation(clause);
  const premier = chargements[0];
  /*
    LE TEMPS DE MISE À DISPOSITION N'EST PLUS CALCULÉ — 28/10/2026.

    Jusqu'ici le document annonçait UNE durée, déduite du POIDS TOTAL et du
    type de rendez-vous (contrat type, article 11). Mohamed a posé le barème
    de TNS, qui est AU MÉTRAGE et s'applique arrêt par arrêt, sur le métrage
    de CHAQUE arrêt.

    Une valeur unique devenait donc fausse : sur une ramasse à 2 ml puis 9 ml,
    le premier arrêt ouvre 45 min et le second 1 h 30. Plutôt que de porter la
    durée sur chaque ligne — « pas besoin de le mettre sur la ligne », 28/10 —
    le document IMPRIME LE BARÈME dans les conditions, et chaque ligne porte
    déjà son métrage dans la colonne Charge. Le client lit les deux et conclut.

    C'est aussi ce qui rend le barème opposable : il est écrit en toutes
    lettres sur le document que le client reçoit.
  */

  const vente = centimes(ordre.prix_vente) ?? 0;
  // L'indexation n'entre dans le total QUE lorsqu'elle est chiffrable ; sinon
  // le total est annoncé « hors indexation ». Jamais entre les deux.
  const surcharge = idx.montant ?? 0;
  const ht = vente + surcharge;
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const siren = client.siret === null ? null : client.siret.slice(0, 9);

  return (
    <div className="conf-cadre">
      <div className="conf-feuille" ref={feuille}>
        <div className="conf-corps">
          <div className="conf-haut">
            <div className="conf-logo">
              <LogoTns />
            </div>
            <div className="conf-emetteur">
              <div className="conf-rs">{TNS.rs}</div>
              {TNS.l1}
              <br />
              {TNS.l2}
              <br />
              {TNS.l3}
              <br />
              {TNS.l4}
            </div>
          </div>

          {/* Le titre n'existe pas sur la facture, dont le type se lit dans
                le bandeau. Ici il est indispensable : le client doit voir d'un
                coup d'œil que ce n'est PAS une facture. */}
          <div className="conf-titre">
            <div className="conf-t">Confirmation de prise en charge</div>
          </div>

          <div className="conf-milieu">
            <div className="conf-renseignement">
              <div className="conf-souligne">Pour tout renseignement :</div>
              {TNS.service}
              <br />
              {TNS.mail}
              <br />
              {TNS.tel}
            </div>
            <div className="conf-client">
              <div className="conf-boite">
                <div className="conf-nom">{client.nom}</div>
                {/* Le numéro de compte sur l'étiquette elle-même : c'est là
                      que le client le cherche, comme sur une étiquette
                      d'expédition. Il libère une case du bandeau. */}
                {client.code !== null && (
                  <div className="conf-cpt">Compte {client.code}</div>
                )}
                <div className="conf-adr">
                  {client.adresse ?? ''}
                  <br />
                  {client.code_postal ?? ''} {client.ville ?? ''}
                  <br />
                  {client.pays ?? ''}
                </div>
              </div>
              <div className="conf-sous">
                <span>
                  {siren === null ? '' : `SIREN ${siren}`}
                  {siren !== null && client.tva !== null ? ' — ' : ''}
                  {client.tva === null ? '' : `TVA ${client.tva}`}
                </span>
                <span>Page 1/{String(pages)}</span>
              </div>
            </div>
          </div>

          <table className="conf-bandeau">
            <tbody>
              <tr>
                <td>
                  <div className="conf-quoi">Confirmation n° :</div>
                  <div className="conf-val">{ordre.numero}</div>
                </td>
                <td>
                  <div className="conf-quoi">Date d’émission :</div>
                  <div className="conf-val">{jourLong(aujourdhui)}</div>
                </td>
                <td>
                  <div className="conf-quoi">Votre référence :</div>
                  <div className="conf-val">{ordre.reference_client ?? '—'}</div>
                </td>
                {/* « Mode de règlement » porterait un DÉLAI : sur la facture
                      cette case porte le mode seul. Le libellé suit la valeur. */}
                <td>
                  <div className="conf-quoi">Conditions de règlement :</div>
                  <div className="conf-val">
                    {client.conditions_paiement ?? 'Selon conditions en vigueur'}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <table className="conf-lignes">
            <thead>
              <tr>
                <th style={{ width: '8.5%' }}>Date</th>
                <th style={{ width: '10.5%' }}>Arrêt</th>
                <th style={{ width: '48%' }}>Lieu et adresse</th>
                <th style={{ width: '14%' }}>Créneau</th>
                <th className="conf-n" style={{ width: '19%' }}>
                  Charge
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="conf-groupe">
                <td colSpan={5}>Enlèvement{chargements.length > 1 ? 's' : ''} :</td>
              </tr>
              {chargements.map((e) => (
                <Arret key={`c${String(e.rang)}`} e={e} combien={chargements.length} />
              ))}
              {/* LE SOUS-TOTAL « TOTAL À ENLEVER » EST PARTI — 28/10/2026,
                  demande de Mohamed. Sur un transport à un seul enlèvement il
                  ne faisait que répéter la ligne du dessus.

                  ET `charge()` N'EST PLUS APPELÉE ICI. Ce commentaire disait
                  d'abord l'inverse — que le calcul restait utile au temps de
                  manutention — et c'était vrai une heure durant. Le barème au
                  métrage a ensuite supprimé ce calcul, et la phrase est devenue
                  fausse sans que rien ne proteste. Corrigé le jour même : un
                  commentaire qui ment est pire qu'un commentaire absent.

                  `charge()` SURVIT DANS SON MODULE : l'ordre d'affrètement
                  l'appelle encore (`EcranOrdreAffretement.tsx:220`), et garde
                  d'ailleurs son propre sous-total — c'est un autre document,
                  pour un autre destinataire. */}
              <tr className="conf-groupe">
                <td colSpan={5}>Livraison{livraisons.length > 1 ? 's' : ''} :</td>
              </tr>
              {livraisons.map((e) => (
                <Arret key={`l${String(e.rang)}`} e={e} combien={livraisons.length} />
              ))}
            </tbody>
          </table>

          <div className="conf-pied">
            <table className="conf-moyen">
              <thead>
                <tr>
                  <th colSpan={2}>Marchandise et moyen</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Nature</td>
                  <td className="conf-q">{ordre.marchandise ?? '—'}</td>
                </tr>
                <tr>
                  <td>Véhicule</td>
                  <td className="conf-q">{ordre.gabarit ?? '—'}</td>
                </tr>
                {/* LA LIGNE « TEMPS DE MANUTENTION » EST PARTIE — 28/10/2026.
                    Elle annonçait UNE durée pour tout l'ordre ; le barème au
                    métrage en donne une PAR ARRÊT. Garder une valeur unique
                    aurait été écrire un chiffre faux dès le deuxième arrêt.
                    Le barème est en conditions, au bas du document. */}
              </tbody>
            </table>

            <div className="conf-totaux">
              <div className="conf-l">
                <span>Prix du transport HT</span>
                <span>{euros(vente)}</span>
              </div>
              {/* « incluse » serait FAUX quand la grille porte une clause :
                    le modèle de facture porte une ligne SURCHARGE CARBURANT
                    qui S'AJOUTE. Annoncer l'inverse prépare une contestation. */}
              <div className="conf-l">
                <span>Indexation gazole</span>
                <span>{idx.phrase}</span>
              </div>
              {/* LE DOCUMENT NE PARLE QU'EN HORS TAXES — 24/09/2026, demande
                    de Mohamed : « on ne parle que en HT, enlève toute mention
                    TTC ». La ligne de TVA est partie avec le total TTC : la
                    garder sans total à quoi l'ajouter n'aurait rien voulu dire.
                    Ce n'est pas une facture — les taxes se règlent sur celle-ci. */}
              <div className="conf-l conf-net">
                <span>
                  TOTAL HT{idx.incluse || surcharge !== 0 ? '' : ' hors indexation'}
                </span>
                <span>{euros(ht)} EUR</span>
              </div>
            </div>
          </div>

          <div className="conf-mentions">
            <div className="conf-categorie">
              <b>Sont compris dans le prix :</b> transport&nbsp;·&nbsp;suivi en ligne et
              eCMR&nbsp;·&nbsp;prise de rendez-vous
              {idx.incluse ? (
                <>
                  &nbsp;·&nbsp;<b>indexation gazole</b>.
                </>
              ) : (
                '.'
              )}
              <br />
              {!idx.incluse && (
                <>
                  <b>Facturée en sus :</b> indexation gazole — {idx.detail}.
                  <br />
                </>
              )}
              <b>Sur demande :</b> hayon&nbsp;·&nbsp;manutention&nbsp;·&nbsp;assurance
              ad valorem&nbsp;·&nbsp;échange de
              palettes&nbsp;·&nbsp;contre-remboursement.
            </div>
            <b>
              Ce document vaut accord sur les dates, les lieux et le prix ci-dessus.
            </b>{' '}
            Toute modification doit nous être signalée avant l’enlèvement.
            <br />
            {/* LE RENVOI AU VERSO — 28/10/2026, en même temps que la seconde
                page. Il n'est pas décoratif : des conditions que le client n'a
                pas eues sous les yeux se discutent. Le recto doit DIRE qu'il y
                a un verso et qu'il fait partie du contrat, sans quoi ce verso
                ne vaudrait pas mieux que les « CGV sur simple demande » qu'il
                remplace. */}
            <b>
              Les conditions applicables à ce transport figurent au verso et font
              partie intégrante du présent document.
            </b>
          </div>
        </div>

        {/* LE TALON EST SORTI DU CORPS — 28/10/2026, demande de Mohamed : les
            deux étiquettes « figées juste au-dessus de la bande bleue ».

            AUCUNE RÈGLE CSS N'A CHANGÉ, et c'est tout l'intérêt :
            `.conf-feuille` est DÉJÀ une colonne flexible et `.conf-corps`
            porte `flex: 1 1 auto`. En devenant le FRÈRE du corps au lieu de
            son enfant, le talon est poussé en bas de page par l'étirement du
            corps — rien à positionner, rien à calculer.

            PAS DE `position: absolute`, qui aurait été la solution évidente et
            la mauvaise : un talon absolu chevauche le texte dès qu'un document
            s'allonge — une ramasse à six enlèvements. Ici, si le contenu
            grandit, le talon redescend naturellement, et s'il ne tient plus il
            passe entier à la page suivante (`break-inside: avoid`, déjà posé
            dans le bloc d'impression). */}
        <div className="conf-talon">
          <div>
            <div className="conf-titre-talon">
              Confirmation {ordre.numero}
              {client.code === null ? '' : ` — Client ${client.code}`}
            </div>
            <div className="conf-gros">
              {euros(ht)} EUR HT
              {premier !== undefined && (
                <span className="conf-maigre">
                  {' '}
                  — enlèvement le {jourLong(premier.jour)}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="conf-titre-talon">Une question sur ce transport ?</div>
            <div>
              {TNS.service} — {TNS.tel}
            </div>
            <div>{TNS.mail}</div>
            <div>Rappelez la référence {ordre.numero}</div>
          </div>
        </div>

        <div className="conf-bande" />
      </div>

      {/* LE VERSO — 28/10/2026, décidé avec Mohamed : « deux pages ».

          POURQUOI UNE SECONDE FEUILLE ET NON UN DÉBORDEMENT : le recto porte
          LE TRANSPORT — qui, quoi, où, quand, combien — et doit rester lisible
          d'un coup d'œil. Les conditions, elles, se lisent une fois et se
          relisent en litige. Les mêler aurait tassé le recto pour un texte que
          personne ne lit au moment de la prise en charge.

          LA FEUILLE EST AUTONOME : elle rappelle l'émetteur et le numéro de
          confirmation, parce qu'une page détachée du recto doit pouvoir être
          rattachée à son transport. */}
      <div className="conf-feuille" ref={verso}>
        <div className="conf-corps">
          <div className="conf-haut">
            <div className="conf-rs">{TNS.rs}</div>
            <div>
              Confirmation {ordre.numero} — Page {String(pages)}/{String(pages)}
            </div>
          </div>

          <div className="conf-titre">
            <span className="conf-t">CONDITIONS DU TRANSPORT</span>
          </div>

          <div className="conf-mentions">
            {/* LE BARÈME DE TNS, posé par Mohamed le 28/10/2026. Il remplace
                les durées du contrat type (article 11), qui se calculaient au
                POIDS et selon le type de rendez-vous.

                IL S'EN ÉCARTE DANS LES DEUX SENS : sur un semi complet sans
                rendez-vous, le contrat type accorde 3 h et ce barème 2 h —
                moins favorable au client ; avec rendez-vous fixe, 1 h contre
                2 h — plus favorable. Le contrat type ne s'applique qu'À DÉFAUT
                de convention écrite : ce barème n'est donc opposable que parce
                qu'il est imprimé ici ET que le recto porte « vaut accord » et
                renvoie à cette page. Les trois tiennent ensemble.

                LES BORNES SE TOUCHENT, sans quoi 7,95 ml ne tomberait nulle
                part. Mohamed a donné 0-3 / 3,1-7,9 / 8-10 / 10,1-13,6 ; les
                intervalles vides sont rattachés à la tranche du dessous, et
                au-delà de 13,6 ml — « ça n'existe pas », mais une saisie
                fautive, si — c'est la tranche haute qui s'applique. */}
            {/* EN TEXTE ET NON EN GRILLE — 28/10/2026, après l'avoir essayé en
                grille. Au verso, les neuf clauses se lisent comme un corps de
                conditions : une table au milieu rompait la colonne et donnait
                à cette clause un poids qu'elle n'a pas plus que les autres. Au
                recto, où le barème était seul, la grille se justifiait. */}
            <div className="conf-categorie">
              <b>1. Mise à disposition du véhicule.</b> Le temps accordé à chaque
              arrêt est fonction du métrage chargé ou déchargé à cet arrêt :{' '}
              <b>45 min</b> jusqu’à 3 ml, <b>1 h</b> de plus de 3 à 8 ml,{' '}
              <b>1 h 30</b> de 8 à 10 ml, <b>2 h</b> au-delà de 10 ml. Ce temps est
              décompté à partir de l’heure de rendez-vous ou du début de la plage
              convenue. Au-delà, l’immobilisation du véhicule et de l’équipage est
              facturée <b>35,00 € HT par demi-heure entamée</b>.
            </div>

            {/* RÉÉCRITE LE 28/10/2026. La première version disait que les
                créneaux étaient « donnés à titre indicatif ». Elle protégeait
                sur le papier et se retournait contre TNS : un transporteur qui
                écrit noir sur blanc que ses horaires n'engagent à rien dit le
                contraire de ce qu'il vend, et le client le lit aussi.

                CELLE-CI NE NIE PAS LE RENDEZ-VOUS — elle nomme les trois causes
                de retard qui ne sont pas le fait du transporteur, et qui sont
                justement les plus fréquentes. On défend moins large, mais on
                peut l'invoquer sans se contredire. */}
            <div className="conf-categorie">
              <b>2. Horaires et délais.</b> Les créneaux indiqués au recto sont les
              rendez-vous convenus. Un retard résultant des conditions de
              circulation, des conditions d’accès au site, ou d’une immobilisation
              subie à un arrêt précédent ne constitue pas un manquement du
              transporteur. Tout engagement de délai garanti fait l’objet d’un
              accord écrit distinct.
            </div>

            <div className="conf-categorie">
              <b>3. Déclarations du donneur d’ordre.</b> Il répond de l’exactitude
              des mentions portées à l’ordre de transport : nature, poids, nombre,
              dimensions et, le cas échéant, classement de la marchandise
              dangereuse. Tout surcoût résultant d’une déclaration inexacte ou
              incomplète lui est facturé.
            </div>

            <div className="conf-categorie">
              <b>4. Accès aux lieux.</b> Le donneur d’ordre garantit que les lieux
              d’enlèvement et de livraison sont accessibles au véhicule annoncé.
              Un transport rendu impossible de ce fait est facturé comme exécuté.
            </div>

            <div className="conf-categorie">
              <b>5. Chargement, calage et arrimage.</b> Sauf convention écrite
              contraire, ils sont assurés par le donneur d’ordre ou son
              représentant, sous leur responsabilité. Toute manutention effectuée
              par notre chauffeur est facturée <b>50,00 € HT par arrêt</b>.
            </div>

            <div className="conf-categorie">
              <b>6. Palettes et supports.</b> L’échange des palettes et supports
              incombe au donneur d’ordre. Les supports non restitués à
              l’enlèvement ou à la livraison sont facturés.
            </div>

            <div className="conf-categorie">
              <b>7. Empêchement à la livraison.</b> En cas d’absence ou de refus du
              destinataire, d’adresse inexacte, ou de marchandise non présentée à
              l’enlèvement, les frais d’immobilisation, de garde, de retour ou de
              réacheminement sont à la charge du donneur d’ordre.
            </div>

            <div className="conf-categorie">
              <b>8. Sous-traitance.</b> Le transporteur peut confier tout ou partie
              de l’exécution du transport à un transporteur substitué, sous sa
              responsabilité.
            </div>

            <div className="conf-categorie">
              {/* LA RÉCLAMATION DES FRAIS A ÉTÉ RETIRÉE — 28/10/2026. La clause
                  disait que l'immobilisation due à un cas de force majeure
                  restait due au transporteur. Mohamed l'a tranché lui-même :
                  « on ne pourra pas réclamer ». Un événement qui échappe aux
                  DEUX parties ne peut pas être facturé à une seule ; une clause
                  qu'on n'oserait pas invoquer affaiblit toutes les autres. */}
              <b>9. Force majeure.</b> Le transporteur n’est pas responsable des
              conséquences d’un événement échappant à son contrôle, notamment
              blocage de route, intempéries ou mouvement social.
            </div>

            {/* LES DEUX MENTIONS VÉRIFIÉES À LA SOURCE, 28/10/2026. Elles ne
                créent aucun droit : elles rappellent la loi. Leur intérêt est
                qu'un client informé ne pourra pas plaider l'ignorance du délai
                de trois jours, qui est court et que presque personne ne
                connaît. */}
            <div className="conf-categorie">
              <b>10. Réserves.</b> Toute perte ou avarie doit faire l’objet de
              réserves écrites, précises et motivées sur le document de transport
              à la livraison, puis être confirmée au transporteur{' '}
              <b>dans les trois jours</b> suivant la réception, jours fériés non
              compris, par lettre recommandée ou acte extrajudiciaire. À défaut,
              toute action est éteinte (article L.133-3 du code de commerce).
            </div>

            <div className="conf-categorie">
              <b>11. Indemnisation.</b> Les indemnités sont limitées conformément au
              contrat type applicable, sauf déclaration de valeur acceptée par le
              transporteur avant l’enlèvement.
            </div>

            <div className="conf-categorie">
              <b>12. Prescription.</b> Toute action née du contrat de transport se
              prescrit par <b>un an</b> (article L.133-6 du code de commerce).
            </div>

            <div className="conf-categorie">
              <b>13. Paiement.</b> Tout retard de paiement entraîne de plein droit
              des pénalités de retard et une indemnité forfaitaire de{' '}
              <b>40 €</b> pour frais de recouvrement. Le transporteur dispose en
              outre de l’action directe en paiement contre l’expéditeur et le
              destinataire (article L.132-8 du code de commerce).
            </div>

            {/* LA CLAUSE QUI PROTÈGE LA TRÉSORERIE — 28/10/2026. Le cas est
                connu de tout transporteur : une palette abîmée, et le client
                retient la facture entière en attendant que « ça se règle ».
                Cette clause sépare les deux : le transport se paie, le litige
                s'instruit à part. */}
            <div className="conf-categorie">
              <b>14. Compensation.</b> Aucune compensation ne peut être opérée
              unilatéralement entre le prix du transport et une créance invoquée
              au titre d’un litige. Le prix reste exigible à son échéance, le
              litige étant traité séparément.
            </div>

            {/* L'ANNULATION — 28/10/2026. Les trois paliers sont une PROPOSITION
                en attente de validation par Mohamed ; ils ne viennent pas de
                lui.

                CE QUI EST VÉRIFIÉ : le contrat type (article 15) ouvre droit à
                indemnité pour une annulation à moins de 24 h, mais SUR
                PRÉJUDICE PROUVÉ, et plafonnée au prix du transport. Tout
                l'intérêt d'un forfait écrit ici est de dispenser TNS de cette
                preuve ; le plafond du texte, lui, est respecté — on ne dépasse
                jamais 100 %.

                POURQUOI 50 % ET NON 100 % entre 24 h et l'enlèvement : à
                vingt-trois heures d'avance, un lot se recase souvent. Réclamer
                la totalité alors que le camion a retrouvé du fret serait
                difficile à tenir devant un client qui le sait. */}
            <div className="conf-categorie">
              <b>15. Annulation.</b> Toute annulation par le donneur d’ordre donne
              lieu à une indemnité : <b>sans frais</b> plus de 24 h avant l’heure
              d’enlèvement convenue ; <b>50 % du prix</b> du transport moins de
              24 h avant ; <b>100 % du prix</b> lorsque le véhicule est déjà en
              route, présenté sur site, ou lorsque le chargement n’est pas
              présenté.
            </div>

            <div className="conf-categorie">
              <b>16. Droit applicable.</b> À défaut de convention écrite contraire,
              le contrat type applicable de plein droit régit les relations entre
              les parties.
            </div>
          </div>
        </div>

        <div className="conf-bande" />
      </div>
    </div>
  );
}
